import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  QrCode,
  Plus,
  Archive,
  BarChart3,
  MoreHorizontal,
  ShieldCheck,
  User,
  HelpCircle,
  LogOut,
  ClipboardList,
  Sparkles,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useHelp } from '../contexts/HelpContext';
import { useMobileChrome } from '../contexts/MobileChromeContext';
import { useBodyScrollLock } from './ui/utils';
import { logger } from '../utils/logger';

/**
 * Bottom tab bar visible only on phones (< md). Pairs with the existing
 * top header (which keeps the brand, dark-mode toggle, environment
 * chip, logout). Five primary destinations are always thumb-reachable;
 * the sixth "More" slot opens a sheet with the secondary destinations
 * (Stats, Liquids, Profile, Help, Logout, and — for admins — Admin).
 *
 * Primary slots: Home, Scan, Create, Catalog, Projects. Stats lives in
 * the More sheet because warehouse staff hit Projects more often during
 * the day than the read-only Stats page.
 *
 * Catalog uses an onClick navigate() to match Layout.tsx's behavior
 * (state-preserving navigation; preserves filters when the user came
 * from a barrel detail page).
 */
const MobileTabBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, currentUser, logout } = useAuth();
  const { open: openHelp } = useHelp();
  const { tabBarHiddenCount } = useMobileChrome();
  const [showMore, setShowMore] = useState(false);

  useBodyScrollLock(showMore);

  // Close the More sheet on route change so the user doesn't land on a
  // new page with the sheet still over the content.
  useEffect(() => {
    setShowMore(false);
  }, [location.pathname]);

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const handleCatalogClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/catalog');
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      logger.error('Failed to log out', error);
    }
  };

  const tabClasses = (active: boolean) =>
    `flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] text-[11px] font-medium transition-colors focus-ring rounded-md ${
      active
        ? 'text-blue-600 dark:text-blue-400'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
    }`;

  const moreItemClass =
    'flex items-center gap-3 w-full px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 focus-ring text-left transition-colors';

  // When a sticky action bar is mounted on a long form, the tab bar
  // hides itself so the two don't stack on top of each other.
  const hidden = tabBarHiddenCount > 0;

  return (
    <>
      <nav
        aria-label="Primary"
        aria-hidden={hidden}
        className={`md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 dark:bg-dark-800/95 backdrop-blur border-t border-gray-200 dark:border-dark-700 pb-safe transition-transform ${
          hidden ? 'translate-y-full pointer-events-none' : 'translate-y-0'
        }`}
      >
        <div className="flex items-stretch px-1">
          <Link to="/" className={tabClasses(isActive('/'))} aria-current={isActive('/') ? 'page' : undefined}>
            <Home className="w-5 h-5" />
            <span>Home</span>
          </Link>
          <Link to="/scan" className={tabClasses(isActive('/scan'))} aria-current={isActive('/scan') ? 'page' : undefined}>
            <QrCode className="w-5 h-5" />
            <span>Scan</span>
          </Link>
          <Link to="/create" className={tabClasses(isActive('/create'))} aria-current={isActive('/create') ? 'page' : undefined}>
            <Plus className="w-5 h-5" />
            <span>Create</span>
          </Link>
          <button
            type="button"
            onClick={handleCatalogClick}
            className={tabClasses(isActive('/catalog'))}
            aria-current={isActive('/catalog') ? 'page' : undefined}
          >
            <Archive className="w-5 h-5" />
            <span>Catalog</span>
          </button>
          <Link to="/projects" className={tabClasses(isActive('/projects'))} aria-current={isActive('/projects') ? 'page' : undefined}>
            <ClipboardList className="w-5 h-5" />
            <span>Projects</span>
          </Link>
          <button
            type="button"
            onClick={() => setShowMore(true)}
            className={tabClasses(false)}
            aria-haspopup="dialog"
            aria-expanded={showMore}
          >
            <MoreHorizontal className="w-5 h-5" />
            <span>More</span>
          </button>
        </div>
      </nav>

      {/* "More" sheet — bottom-anchored so it visually grows out of the
          tab bar. Backdrop click and ESC close. Body scroll is locked
          while open via useBodyScrollLock. */}
      {showMore && (
        <div
          className="md:hidden fixed inset-0 z-50 flex items-end"
          role="dialog"
          aria-modal="true"
          aria-label="More options"
          onClick={() => setShowMore(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowMore(false);
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full bg-white dark:bg-dark-800 rounded-t-2xl shadow-xl border-t border-gray-200 dark:border-dark-700 pb-safe"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-dark-700">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                  {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Signed in'}
                </p>
                {currentUser?.email && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {currentUser.email}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowMore(false)}
                className="w-11 h-11 -mr-2 inline-flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg focus-ring"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-3 space-y-1">
              {/* Stats — promoted from the primary tab strip to make
                  room for Projects (more frequently hit during the
                  workday). Still one tap away from the More sheet. */}
              <Link to="/stats" className={moreItemClass}>
                <BarChart3 className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                <span>Stats</span>
              </Link>
              {/* Liquids (route path is still /blends). Phones had no
                  entry point to it at all — the desktop nav has had one
                  since the blends index shipped. */}
              <Link to="/blends" className={moreItemClass}>
                <Sparkles className="w-5 h-5 text-purple-500 dark:text-purple-400" />
                <span>Liquids</span>
              </Link>
              {role === 'admin' && (
                <Link to="/admin" className={moreItemClass}>
                  <ShieldCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <span>Admin</span>
                </Link>
              )}
              <Link to="/profile" className={moreItemClass}>
                <User className="w-5 h-5" />
                <span>Profile</span>
              </Link>
              <button
                type="button"
                onClick={() => {
                  setShowMore(false);
                  openHelp();
                }}
                className={moreItemClass}
              >
                <HelpCircle className="w-5 h-5" />
                <span>Help</span>
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className={moreItemClass}
              >
                <LogOut className="w-5 h-5 text-red-500" />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileTabBar;
